import React, { useState } from 'react';
import { Upload, ChevronLeft, ChevronRight, User, ShieldCheck, LogOut, Table2, MessageSquare, Package, Map, LayoutDashboard, Inbox, Activity, Database, Home, Calendar, FileText } from 'lucide-react';
import LogoutDialog from './LogoutDialog';
import { useSidebar } from '../hooks/useSidebar';
import SidebarTabItem from './sidebar/SidebarTabItem';
import SidebarSection from './sidebar/SidebarSection';
import SiteSelector from './sidebar/SiteSelector';
import ThemeToggle from './sidebar/ThemeToggle';
import PortfolioPicker from './sidebar/PortfolioPicker';
import PortfolioManagerModal from './sidebar/PortfolioManagerModal';

interface SidebarProps {
  onAddTab: (tabId: string) => void;
  onPopOutTab: (tabId: string) => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  onToggleChat?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ onAddTab, onPopOutTab, isCollapsed, onToggleCollapse, onToggleChat }) => {
  const {
    resolvedTheme,
    canUpload,
    showAdminButton,
    adminLabel,
    isSuperAdmin,
    portfolios,
    selectedPortfolioId,
    handlePortfolioSelect,
    currentSite,
    portfolioSites,
    sitesLoading,
    hasQAResultsForSite,
    portfolioTabsOpen,
    togglePortfolioTabs,
    siteTabsOpen,
    toggleSiteTabs,
    qaTabsOpen,
    toggleQaTabs,
    settingsOpen,
    toggleSettings,
    isDropdownOpen,
    setIsDropdownOpen,
    siteSearchTerm,
    setSiteSearchTerm,
    dropdownPosition,
    dropdownRef,
    siteSelectorButtonRef,
    siteDropdownRef,
    handleSiteSelect,
    createSiteLoading,
    handleCreateSiteWithAsBuilt,
    showLogoutDialog,
    setShowLogoutDialog,
    handleLogout,
    isLoggingOut,
    tabs,
    qaTabsAlways,
    qaTabsWhenResults,
  } = useSidebar(onAddTab);

  const [showPortfolioManager, setShowPortfolioManager] = useState(false);

  return (
    <div className={`eg-sidebar ${isCollapsed ? 'w-16' : 'w-64'}`}>
      {/* Collapse/Expand Button */}
      <button
        onClick={onToggleCollapse}
        className="absolute top-14 -right-3 z-[9999] w-6 h-6 bg-engramiq-green hover:bg-engramiq-green/80 rounded-full flex items-center justify-center shadow-lg transition-all"
        title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {isCollapsed ? (
          <ChevronRight className="w-4 h-4 text-engramiq-dark-blue" />
        ) : (
          <ChevronLeft className="w-4 h-4 text-engramiq-dark-blue" />
        )}
      </button>

      {/* Logo Header */}
      <div className={`p-4 ${isCollapsed ? 'hidden' : ''}`}>
        <div className="flex items-center justify-center">
          <img
            src={resolvedTheme === 'dark' ? '/LOGO MARK AND TYPE - CMYK-02 (1).png' : '/logo-dark.png'}
            alt="Engramiq Logo"
            className="max-h-12 max-w-full object-contain"
          />
        </div>
      </div>

      {/* Upload Button */}
      {canUpload && (
        <div className={`p-4 ${isCollapsed ? 'px-2' : ''}`}>
          {isCollapsed ? (
            <div className="relative group flex justify-center">
              <button
                onClick={handleCreateSiteWithAsBuilt}
                disabled={createSiteLoading}
                title="Upload As-Builts"
                className="eg-sidebar-icon-btn disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Upload className="w-5 h-5 text-engramiq-green" />
              </button>
              <span className="eg-sidebar-tooltip">Upload As-Builts</span>
            </div>
          ) : (
            <button
              onClick={handleCreateSiteWithAsBuilt}
              disabled={createSiteLoading}
              title="Upload As-Builts"
              className="w-full px-4 py-2 bg-gradient-green-blue hover:bg-engramiq-green rounded-lg text-sm text-engramiq-dark-blue hover:text-engramiq-dark-blue transition-all hover:shadow-engramiq flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Upload className="w-4 h-4" />
              Upload As-Builts
            </button>
          )}
        </div>
      )}

      {/* Main Content Area */}
      <div className={`flex-1 flex flex-col min-h-0 overflow-hidden ${isCollapsed ? 'items-center gap-3 px-2' : ''}`}>
        {isCollapsed ? (
          <>
            <div className="relative group">
              <button onClick={() => onAddTab('home')} className="eg-sidebar-icon-btn" title="Home">
                <Home className="w-5 h-5 text-engramiq-green" />
              </button>
              <span className="eg-sidebar-tooltip">Home</span>
            </div>
            <div className="relative group">
              <button onClick={() => onAddTab('portfoliodashboard')} className="eg-sidebar-icon-btn" title="Dashboard">
                <LayoutDashboard className="w-5 h-5 text-engramiq-green" />
              </button>
              <span className="eg-sidebar-tooltip">Dashboard</span>
            </div>
            <div className="relative group">
              <button onClick={() => onAddTab('projectinbox')} className="eg-sidebar-icon-btn" title="Inbox">
                <Inbox className="w-5 h-5 text-engramiq-green" />
              </button>
              <span className="eg-sidebar-tooltip">Inbox</span>
            </div>
            <div className="relative group">
              <button onClick={() => onAddTab('portfoliocomponents')} className="eg-sidebar-icon-btn" title="Portfolio Components">
                <Package className="w-5 h-5 text-engramiq-green" />
              </button>
              <span className="eg-sidebar-tooltip">Components</span>
            </div>
            <div className="relative group">
              <button onClick={() => onAddTab('portfolioeventscalendar')} className="eg-sidebar-icon-btn" title="Events Calendar">
                <Calendar className="w-5 h-5 text-engramiq-green" />
              </button>
              <span className="eg-sidebar-tooltip">Events Calendar</span>
            </div>
            <div className="relative group">
              <button onClick={() => onAddTab('sitestable')} className="eg-sidebar-icon-btn" title="Sites">
                <Table2 className="w-5 h-5 text-engramiq-green" />
              </button>
              <span className="eg-sidebar-tooltip">Sites</span>
            </div>
            <div className="w-8 eg-divider" />
            <div className="relative group">
              <button onClick={() => onAddTab('dataroom')} className="eg-sidebar-icon-btn" title="Dataroom">
                <Database className="w-5 h-5 text-engramiq-green" />
              </button>
              <span className="eg-sidebar-tooltip">Dataroom</span>
            </div>
            <div className="relative group">
              <button onClick={() => onAddTab('contracts')} className="eg-sidebar-icon-btn" title="Contracts">
                <FileText className="w-5 h-5 text-engramiq-green" />
              </button>
              <span className="eg-sidebar-tooltip">Contracts</span>
            </div>
            <div className="flex-1 overflow-y-auto flex flex-col items-center gap-3 py-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {tabs.map(tab => (
                <SidebarTabItem key={tab.id} tab={tab} onAddTab={onAddTab} isCollapsed />
              ))}
              {qaTabsAlways.map(tab => (
                <SidebarTabItem key={tab.id} tab={tab} onAddTab={onAddTab} isCollapsed />
              ))}
              {hasQAResultsForSite && qaTabsWhenResults.map(tab => (
                <SidebarTabItem key={tab.id} tab={tab} onAddTab={onAddTab} isCollapsed />
              ))}
            </div>
          </>
        ) : (
          <div className="flex-1 min-h-0 overflow-y-auto px-4">
            {/* Portfolio Tabs */}
            <SidebarSection title="Portfolio Tabs" isOpen={portfolioTabsOpen} onToggle={togglePortfolioTabs}>
              <div className="px-4 pb-3 space-y-2 relative">
                {/* Portfolio Picker */}
                <PortfolioPicker
                  portfolios={portfolios}
                  selectedPortfolioId={selectedPortfolioId}
                  onSelect={handlePortfolioSelect}
                  onManage={() => setShowPortfolioManager(true)}
                  showAllSitesOption={isSuperAdmin}
                />

                {/* Site Selector */}
                <SiteSelector
                  currentSite={currentSite}
                  portfolioSites={portfolioSites}
                  sitesLoading={sitesLoading}
                  isDropdownOpen={isDropdownOpen}
                  setIsDropdownOpen={setIsDropdownOpen}
                  siteSearchTerm={siteSearchTerm}
                  setSiteSearchTerm={setSiteSearchTerm}
                  dropdownPosition={dropdownPosition}
                  dropdownRef={dropdownRef}
                  siteSelectorButtonRef={siteSelectorButtonRef}
                  siteDropdownRef={siteDropdownRef}
                  handleSiteSelect={handleSiteSelect}
                />
                <button
                  onClick={() => onAddTab('home')}
                  className="eg-sidebar-item flex items-center gap-2 group"
                >
                  <Home className="w-4 h-4 text-engramiq-green" />
                  <span>Home</span>
                </button>
                <button
                  onClick={() => onAddTab('portfoliodashboard')}
                  className="eg-sidebar-item flex items-center gap-2 group"
                >
                  <LayoutDashboard className="w-4 h-4 text-engramiq-green" />
                  <span>Portfolio Dashboard</span>
                </button>
                <button
                  onClick={() => onAddTab('projectinbox')}
                  className="eg-sidebar-item flex items-center gap-2 group"
                >
                  <Inbox className="w-4 h-4 text-engramiq-green" />
                  <span>Inbox</span>
                </button>
                <button
                  onClick={() => onAddTab('portfoliocomponents')}
                  className="eg-sidebar-item flex items-center gap-2 group"
                >
                  <Package className="w-4 h-4 text-engramiq-green" />
                  <span>Portfolio Components</span>
                </button>
                <button
                  onClick={() => onAddTab('portfolioeventscalendar')}
                  className="eg-sidebar-item flex items-center gap-2 group"
                >
                  <Calendar className="w-4 h-4 text-engramiq-green" />
                  <span>Events Calendar</span>
                </button>
                <button
                  onClick={() => onAddTab('sitestable')}
                  className="eg-sidebar-item flex items-center gap-2 group"
                >
                  <Table2 className="w-4 h-4 text-engramiq-green" />
                  <span>Sites</span>
                </button>
              </div>
            </SidebarSection>

            {/* Site Tabs */}
            <SidebarSection title="Site Tabs" isOpen={siteTabsOpen} onToggle={toggleSiteTabs}>
              <div className="px-4 pb-3 space-y-1">
                <button
                  onClick={() => onAddTab('siteanalysis')}
                  className="eg-sidebar-item flex items-center gap-2 group"
                >
                  <Activity className="w-4 h-4 text-engramiq-green" />
                  <span>Site Analysis</span>
                </button>
                <button
                  onClick={() => onAddTab('dataroom')}
                  className="eg-sidebar-item flex items-center gap-2 group"
                >
                  <Database className="w-4 h-4 text-engramiq-green" />
                  <span>Dataroom</span>
                </button>
                <button
                  onClick={() => onAddTab('contracts')}
                  className="eg-sidebar-item flex items-center gap-2 group"
                >
                  <FileText className="w-4 h-4 text-engramiq-green" />
                  <span>Contracts</span>
                </button>
                {tabs.map(tab => (
                  <SidebarTabItem key={tab.id} tab={tab} onAddTab={onAddTab} isCollapsed={false} />
                ))}
              </div>
            </SidebarSection>

            {/* QA Tabs */}
            <SidebarSection title="Drawing QA" isOpen={qaTabsOpen} onToggle={toggleQaTabs}>
              <div className="px-4 pb-3 space-y-1">
                {qaTabsAlways.map(tab => (
                  <SidebarTabItem key={tab.id} tab={tab} onAddTab={onAddTab} isCollapsed={false} />
                ))}
                {hasQAResultsForSite && qaTabsWhenResults.map(tab => (
                  <SidebarTabItem key={tab.id} tab={tab} onAddTab={onAddTab} isCollapsed={false} />
                ))}
              </div>
            </SidebarSection>
          </div>
        )}
      </div>

      {/* Settings Section */}
      <div className={`px-4 mt-auto ${isCollapsed ? 'px-2' : ''}`}>
        {!isCollapsed && (
          <SidebarSection title="Settings" isOpen={settingsOpen} onToggle={toggleSettings}>
            <div className="px-4 pb-3 space-y-2">
              <ThemeToggle isCollapsed={false} />
              <div>
                <button onClick={() => onAddTab('userprofile')} className="eg-sidebar-item group" title="User Profile">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-engramiq-green flex-shrink-0" />
                    <span className="text-sm eg-text-primary">My Profile</span>
                  </div>
                </button>
              </div>
              {showAdminButton && (
                <div>
                  <button onClick={() => onAddTab('adminview')} className="eg-sidebar-item group">
                    <div className="flex items-center gap-2">
                      <ShieldCheck className="w-4 h-4 text-engramiq-green flex-shrink-0" />
                      <span className="text-sm eg-text-primary">{adminLabel}</span>
                    </div>
                  </button>
                </div>
              )}
            </div>
          </SidebarSection>
        )}
        {isCollapsed && (
          <div className="flex flex-col items-center gap-3">
            <ThemeToggle isCollapsed />
            <div className="relative group">
              <button onClick={() => onAddTab('userprofile')} className="eg-sidebar-icon-btn" title="User Profile">
                <User className="w-4 h-4 text-engramiq-green flex-shrink-0" />
              </button>
              <span className="eg-sidebar-tooltip">My Profile</span>
            </div>
            {showAdminButton && (
              <div className="relative group">
                <button onClick={() => onAddTab('adminview')} className="eg-sidebar-icon-btn">
                  <ShieldCheck className="w-4 h-4 text-engramiq-green flex-shrink-0" />
                </button>
                <span className="eg-sidebar-tooltip">{adminLabel}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* AI Chat Button */}
      {onToggleChat && (
        <div className={`px-4 pb-2 ${isCollapsed ? 'px-2' : ''}`}>
          {isCollapsed ? (
            <div className="relative group flex justify-center">
              <button onClick={onToggleChat} className="eg-sidebar-icon-btn" title="AI Assistant">
                <MessageSquare className="w-5 h-5 text-[#17c480]" />
              </button>
              <span className="eg-sidebar-tooltip">AI Assistant</span>
            </div>
          ) : (
            <button
              onClick={onToggleChat}
              className="w-full px-3 py-2 rounded-lg border border-[#17c480]/20 bg-[#17c480]/5
                         hover:bg-[#17c480]/10 transition-colors flex items-center gap-2 group"
            >
              <MessageSquare className="w-4 h-4 text-[#17c480]" />
              <span className="text-sm text-[var(--color-text-primary)]">AI Assistant</span>
              <span className="ml-auto px-1.5 py-0.5 text-[9px] font-medium rounded bg-[#17c480]/10 text-[#17c480]">Preview</span>
            </button>
          )}
        </div>
      )}

      {/* Logout Button */}
      <div className={`p-4 ${isCollapsed ? 'px-2' : ''}`}>
        <div className={`${isCollapsed ? 'relative group' : ''}`}>
          <button
            onClick={() => setShowLogoutDialog(true)}
            disabled={isLoggingOut}
            className={`${isCollapsed
              ? 'eg-sidebar-icon-btn'
              : 'eg-sidebar-item flex items-center gap-2 group disabled:opacity-50 disabled:cursor-not-allowed'
            }`}
          >
            <LogOut className="w-4 h-4 text-engramiq-green" />
            {!isCollapsed && <span>{isLoggingOut ? 'Signing out...' : 'Logout'}</span>}
          </button>
          {isCollapsed && <span className="eg-sidebar-tooltip">Logout</span>}
        </div>
      </div>

      {/* Logout Confirmation Dialog */}
      <LogoutDialog
        isOpen={showLogoutDialog}
        onClose={() => setShowLogoutDialog(false)}
        onConfirm={async () => {
          setShowLogoutDialog(false);
          await handleLogout();
        }}
        isLoading={isLoggingOut}
      />

      {/* Portfolio Manager Modal */}
      <PortfolioManagerModal
        isOpen={showPortfolioManager}
        onClose={() => setShowPortfolioManager(false)}
        selectedPortfolioId={selectedPortfolioId}
        onSelectPortfolio={handlePortfolioSelect}
      />
    </div>
  );
};

export default Sidebar;
